import { apiGet } from "./client.js";

/**
 * How this app reads a collection the API pages.
 *
 * `GET /api/tracks`, `/api/playlists` and `/api/feeds` answer one page and a cursor
 * to the next (`packages/contracts` — `CollectionPageQuerySchema`). Two things read
 * them, and they want different things:
 *
 * - a screen that shows the collection reads **one page at a time** and asks for the
 *   next when the operator reaches the end of what he has;
 * - a control that must know the whole set before it can be right — the console's
 *   count of every feed, a select of every playlist a feed could be pointed at —
 *   **follows the cursor to the end**, because a select that quietly offers the first
 *   fifty of sixty playlists is worse than a slow one.
 *
 * Both are here so no screen has to remember which of the two it is, and so a
 * paginated route can never be read as if it were the whole set by accident.
 */

export interface Page<T> {
  items: T[];
  /** Null means this was the last page, not that the read failed. */
  nextCursor: string | null;
}

export interface PageRequest {
  cursor?: string | null;
  limit?: number;
  /** The library's search term. Applied by the API before the page is cut. */
  q?: string;
  signal?: AbortSignal | undefined;
}

/**
 * A collection is never read for more than this many pages in one sweep. It is a
 * bound on a loop that follows a cursor the API controls, not a limit on the product
 * — at the API's 200-row cap it is 20 000 rows, and a sweep that reaches it stops and
 * says so rather than paging forever.
 */
const MAXIMUM_SWEEP_PAGES = 100;

function pageUrl(path: string, request: PageRequest): string {
  const query = new URLSearchParams();
  if (request.limit !== undefined) query.set("limit", String(request.limit));
  if (request.cursor) query.set("cursor", request.cursor);
  if (request.q !== undefined && request.q !== "") query.set("q", request.q);
  const search = query.toString();
  return search === "" ? path : `${path}?${search}`;
}

function nextCursorOf(body: unknown): string | null {
  if (typeof body !== "object" || body === null) return null;
  const value = (body as { next_cursor?: unknown }).next_cursor;
  return typeof value === "string" && value !== "" ? value : null;
}

/** One page, and where the next one starts. */
export async function fetchPage<T>(
  path: string,
  parse: (body: unknown) => T[],
  request: PageRequest = {},
): Promise<Page<T>> {
  const body = await apiGet<unknown>(pageUrl(path, request), {
    signal: request.signal,
  });
  return { items: parse(body), nextCursor: nextCursorOf(body) };
}

/** Every page, for the reads whose answer is only correct if it is complete. */
export async function fetchEveryPage<T>(
  path: string,
  parse: (body: unknown) => T[],
  request: PageRequest = {},
): Promise<T[]> {
  const items: T[] = [];
  let cursor: string | null = request.cursor ?? null;
  for (let sweep = 0; sweep < MAXIMUM_SWEEP_PAGES; sweep += 1) {
    const page: Page<T> = await fetchPage(path, parse, {
      ...request,
      cursor,
      limit: request.limit ?? 200,
    });
    items.push(...page.items);
    cursor = page.nextCursor;
    if (cursor === null) return items;
  }
  return items;
}
